import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BaseServices } from '../../kuba.services';
import { FlexiComplianceService } from './flexi-compliance.service';
import {
  FLEXI_RECURRENCE_TYPES,
  FlexiComplianceActionPointEvent,
  FlexiComplianceActionPointListItem,
  FlexiComplianceActionPointListMetadata,
  FlexiComplianceActionPointStatus,
  FlexiComplianceRating,
  FlexiComplianceRecurrencePreview,
  FlexiComplianceRecurrenceType,
  FlexiComplianceResponsibleParty,
  FlexiComplianceScheduleMode,
  recurrenceTypeKey,
  responsiblePartyNameSuffix
} from './flexi-compliance.models';

/**
 * Type axis of the filter bar. Replaces the former All/Recurring SUB-TABS:
 * they were a filter dressed as navigation over one dataset with one set of
 * columns, so "Recurring 0" was a tab you could click to arrive nowhere, and
 * the interval filter existed only inside one of them — switching tabs silently
 * discarded it. As a filter value all three states live side by side, and
 * "One-off", which the tabs made unreachable, becomes selectable.
 */
export type ActionPointTypeFilter = 'all' | 'oneOff' | 'recurring';

/**
 * Standalone Flexi → Action Points tab. One grid over every action point, with
 * a type/status/interval filter bar, colour-coded badges and grouped meta.
 *
 * Deep link: `?highlight=<actionPointId>` (used by the dashboard card) scrolls
 * to that row and highlights it until the next click, clearing filters first if
 * they would have hidden it.
 *
 * Visibility: appended in flexi.component.ts only when the probe returns
 * at least one row, so this component never renders for businesses
 * without an assessment.
 *
 * Authoring: CSM-only (Admin / assigned HSE advisor). Detected
 * on init by probing the CSM-gated <c>GET /flexi/compliance/{businessId}</c>
 * endpoint — 200 ⇒ can edit, 401/403 ⇒ read-only. We accept the
 * (idempotent) seeding side-effect because the AP tab only renders when
 * an assessment already exists, so the probe always lands on the
 * existing-row branch.
 */
@Component({
  selector: 'flexi-compliance-action-points-list',
  templateUrl: './action-points-list.component.html',
  styleUrls: ['./flexi-compliance.scss'],
  standalone: false
})
export class ActionPointsListComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationService);

  readonly ApStatus = FlexiComplianceActionPointStatus;
  readonly Rating = FlexiComplianceRating;
  readonly Responsible = FlexiComplianceResponsibleParty;
  readonly Recurrence = FlexiComplianceRecurrenceType;
  readonly ScheduleMode = FlexiComplianceScheduleMode;
  readonly recurrenceTypes = FLEXI_RECURRENCE_TYPES;
  readonly recurrenceTypeKey = recurrenceTypeKey;

  /** Type axis — mutually exclusive, rendered as a segmented control. */
  typeFilter: ActionPointTypeFilter = 'all';

  /** When set, the grid is narrowed to this interval. Implies `recurring`. */
  recurrenceFilter: FlexiComplianceRecurrenceType | null = null;

  /** The current page of rows — NOT the whole list. */
  rows: FlexiComplianceActionPointListItem[] = [];
  loading = false;
  error: string | null = null;

  // ---------- Server-side pagination ----------

  /**
   * Ten per page, as the grid's default. Server-side: the endpoint applies the
   * filter bar and returns one page plus whole-list tallies, so nothing here
   * ever holds every action point. That matters for a business with years of
   * recurring commitments behind it.
   */
  readonly pageSize = 10;

  /** In-flight latch for the CSV export, which fetches the whole list. */
  exporting = false;

  /** In-flight latch for the XLSX export, which the server generates. */
  exportingXlsx = false;

  pageNumber = 1;
  totalCount = 0;
  totalPages = 0;

  /**
   * Whole-list tallies from the server, for the filter chips and the
   * skipped-cycle warning. They deliberately describe every action point on the
   * business rather than the page or the filtered set — a chip that counted the
   * page could never tell you what selecting it would show.
   */
  counts: FlexiComplianceActionPointListMetadata = {
    allCount: 0,
    recurringCount: 0,
    oneOffCount: 0,
    openCount: 0,
    inProgressCount: 0,
    completedCount: 0,
    rejectedCount: 0,
    missingCount: 0,
    needsImprovementCount: 0,
    skippedCycleCount: 0
  };

  /**
   * Findings axis: which checkpoint ratings to keep — Mangler (red) and Bør
   * forbedres (yellow). Empty means "no findings filter".
   *
   * This is the axis that makes the action plan workable as a to-do list: the
   * advisor asks "what is still red" and gets exactly the commitments that
   * answer for a standing finding. A row leaves the selection as soon as its
   * action closes and the checkpoint turns green, which is why it is filtered
   * server-side against the live rating rather than a value frozen on the row.
   */
  findingsFilter = new Set<FlexiComplianceRating>();

  /**
   * Selected statuses. Empty means "no status filter".
   *
   * Multi-select rather than the previous single value: "Open plus In progress"
   * — everything still outstanding — is the view people actually want, and one
   * exclusive selection could not express it.
   */
  statusFilter = new Set<FlexiComplianceActionPointStatus>();

  /** True once the CSM probe succeeds — unlocks the status menu and delete buttons. */
  canEdit = false;

  /**
   * Which card has the status dropdown / actions panel open. Only one open
   * at a time keeps the page calm.
   */
  openMenuId: number | null = null;

  /** Per-row in-flight latch so the menu disables while a PUT/DELETE is pending. */
  busyIds = new Set<number>();

  /**
   * Row being edited in the detail dialog, or null when it's closed. Details
   * are CSM-only (the BE silently drops non-CSM field edits), and only while
   * the action point is still outstanding.
   */
  editRow: FlexiComplianceActionPointListItem | null = null;
  editForm = {
    title: '',
    description: '',
    responsible: FlexiComplianceResponsibleParty.Customer,
    due: '',
    recurrenceType: FlexiComplianceRecurrenceType.None
  };
  editSaving = false;

  /**
   * Set while the overdue-completion dialog is open. Holds the server-computed
   * preview so both buttons can be labelled with the deadline they will
   * actually produce.
   */
  scheduleChoiceRow: FlexiComplianceActionPointListItem | null = null;
  schedulePreview: FlexiComplianceRecurrencePreview | null = null;
  scheduleChoiceLoading = false;

  /**
   * Keep-the-original-schedule is pre-selected. In HSE many deadlines are
   * legally tied to a time of year, so the safe default is the one that leaves
   * the compliance cycle where it is.
   */
  selectedScheduleMode: FlexiComplianceScheduleMode = FlexiComplianceScheduleMode.OriginalSchedule;

  /**
   * Row open in the read-only details dialog, or null when it is closed.
   *
   * Separate from `editRow`: editing is CSM-only and refused once an action
   * point is completed, which left a completed row with nowhere to be inspected
   * at all. This is where the completion audit (who closed it, and when) is
   * read — deliberately here and not as a grid column, so the grid stays about
   * outstanding work rather than about who did what.
   */
  detailRow: FlexiComplianceActionPointListItem | null = null;

  /** Timeline panel: the row being inspected, plus its entries. */
  timelineRow: FlexiComplianceActionPointListItem | null = null;
  timelineEntries: FlexiComplianceActionPointEvent[] = [];
  timelineLoading = false;

  /**
   * Row the user arrived to see, from `?highlight=<id>` on the dashboard link.
   * Stays highlighted until the next click anywhere, so it survives reading and
   * scrolling rather than flashing past.
   */
  highlightedId: number | null = null;

  private routeSub?: Subscription;

  /**
   * The highlight target we have already asked the server to locate. Stops a
   * stale or deleted id from bouncing load() forever, since a row that does not
   * exist never turns up in the response either.
   */
  private focusFetchAttemptedFor: number | null = null;

  constructor(
    private service: FlexiComplianceService,
    private confirm: ConfirmationService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.detectCsm();

    // Subscribed rather than read once from the snapshot: clicking a second
    // dashboard row while already on this page changes the param without
    // re-creating the component, and a snapshot read would ignore it.
    //
    // queryParamMap emits its current value synchronously, so the first
    // emission is where the initial load happens — it is the only point that
    // knows whether we arrived with a ?highlight= target, and the target
    // decides which page to ask for.
    let isFirstEmission = true;
    this.routeSub = this.route.queryParamMap.subscribe(params => {
      const raw = params.get('highlight');
      const id = raw ? Number(raw) : NaN;
      const nextId = Number.isFinite(id) ? id : null;

      if (nextId !== this.highlightedId) {
        // New target: allow one page-resolving fetch for it.
        this.focusFetchAttemptedFor = null;
      }
      this.highlightedId = nextId;

      if (isFirstEmission) {
        isFirstEmission = false;
        this.load(this.highlightedId ?? undefined);
        return;
      }
      if (this.highlightedId !== null) this.revealHighlighted();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  /**
   * Any click anywhere drops the highlight — the "until you click" half of the
   * requirement. Bound on the document rather than the component so a click on
   * the surrounding chrome counts too.
   */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.highlightedId !== null) this.highlightedId = null;
  }

  /**
   * Makes the highlighted row actually visible, then scrolls to it.
   *
   * Two things can hide the target, and both have to be undone or the user
   * follows a dashboard link into a grid that does not contain the row they
   * clicked:
   *
   *  - A filter excludes it — arriving with "Completed" deselected and landing
   *    on a completed action point. The filters are dropped: the user asked to
   *    see THIS row, which outranks a filter they set earlier.
   *  - It is on another page. Only the server knows which, so we re-ask with
   *    focusActionPointId and it returns that page.
   *
   * Both are handled by one refetch. `focusFetchAttemptedFor` bounds it to a
   * single attempt per target: a stale or deleted id is simply not in the list,
   * and without the latch the response would trigger the same request again.
   */
  private revealHighlighted(): void {
    if (this.highlightedId === null) return;

    if (!this.rows.some(r => r.id === this.highlightedId)) {
      if (this.loading || this.focusFetchAttemptedFor === this.highlightedId) return;
      this.focusFetchAttemptedFor = this.highlightedId;
      // Locally, not via clearAllFilters(): that reloads, and it would race the
      // focused request issued on the next line.
      if (this.hasActiveFilters) this.clearFiltersLocally();
      this.pageNumber = 1;
      this.load(this.highlightedId);
      return;
    }

    // One tick, so the row exists in the DOM before we look for it.
    setTimeout(() => {
      const el = this.host.nativeElement
        .querySelector<HTMLElement>(`[data-ap-row="${this.highlightedId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /**
   * Fetch the current page.
   *
   * `focusId` asks the server for whichever page holds that action point — the
   * client cannot work that out, since it only ever holds one page of the
   * ordering. Used on first load when arriving from the dashboard's
   * `?highlight=` link.
   */
  load(focusId?: number): void {
    if (focusId) this.focusFetchAttemptedFor = focusId;
    this.loading = true;
    this.error = null;
    this.service.listActionPointsPaged(BaseServices.BusinessId, {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      isRecurring: this.isRecurringFilterValue,
      statuses: Array.from(this.statusFilter),
      sourceRatings: Array.from(this.findingsFilter),
      recurrenceType: this.recurrenceFilter,
      focusActionPointId: focusId ?? null
    }).subscribe({
      next: page => {
        this.rows = page.items;
        // Echoed by the server: with focusActionPointId it decided the page,
        // and with a filter that shortened the list it may have clamped us
        // back to the last one.
        this.pageNumber = page.pageNumber;
        this.totalCount = page.totalCount;
        this.totalPages = page.totalPages;
        this.counts = page.metadata;
        this.loading = false;
        // The query param usually arrives before the rows do, so the reveal has
        // to run again once there is something to reveal.
        this.revealHighlighted();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.rows = [];
        // 403 means the user may not see action points at all. The tab is
        // already hidden for them, so this only happens on a typed/shared URL —
        // say why rather than surfacing the raw server detail as if the load
        // had merely failed.
        this.error = err.status === 403
          ? this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.ACCESS_DENIED')
          : (err.error?.detail
            ?? this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.EMPTY'));
      }
    });
  }

  /**
   * Re-run the query from page one. Any filter change has to reset the page:
   * staying on page 4 of a list that just shrank to two pages shows an empty
   * grid, which reads as "nothing matches" rather than "wrong page".
   */
  private reload(): void {
    this.pageNumber = 1;
    this.load();
  }

  /** Filter-bar type axis expressed the way the endpoint wants it. */
  private get isRecurringFilterValue(): boolean | null {
    if (this.typeFilter === 'recurring') return true;
    if (this.typeFilter === 'oneOff') return false;
    return null;
  }

  // ---------- Pagination ----------

  goToPage(page: number): void {
    const target = Math.min(Math.max(1, page), Math.max(1, this.totalPages));
    if (target === this.pageNumber) return;
    this.pageNumber = target;
    this.load();
  }

  get canGoPrevious(): boolean { return this.pageNumber > 1; }
  get canGoNext(): boolean { return this.pageNumber < this.totalPages; }

  /** 1-based index of the first row on this page, for "x–y of z". */
  get rangeStart(): number {
    return this.totalCount === 0 ? 0 :(this.pageNumber - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalCount);
  }

  private detectCsm(): void {
    // Probe the CSM-gated aggregate endpoint. The AP tab only shows when an
    // assessment already exists (the parent flexi.component.ts probes
    // listActionPoints first), so getOrCreate's seed branch is unreachable
    // here — this is a permission probe, not a write.
    this.service.getOrCreate(BaseServices.BusinessId).subscribe({
      next: () => { this.canEdit = true; },
      error: () => { this.canEdit = false; }
    });
  }

  // ---------- Derived state ----------

  /**
   * The rows to render.
   *
   * All three filter axes are applied by the server now, so this is just the
   * page it returned. Kept as a named getter because the template and
   * revealHighlighted both read it, and filtering here as well would narrow the
   * result twice — visibly, once the list is longer than one page.
   */
  get filteredRows(): FlexiComplianceActionPointListItem[] {
    return this.rows;
  }

  // Every count below is the server's whole-list tally, not something derived
  // from `rows` — `rows` is one page of ten. Counting the page would make each
  // filter chip describe the page it is sitting on instead of what clicking it
  // would show, and would let a filter hide a skipped cycle.

  get recurringCount(): number { return this.counts.recurringCount; }

  get oneOffCount(): number { return this.counts.oneOffCount; }

  /** Series with a compliance gap, for the warning strip above the grid. */
  get skippedCycleCount(): number { return this.counts.skippedCycleCount; }

  get openCount(): number { return this.counts.openCount; }
  get inProgressCount(): number { return this.counts.inProgressCount; }
  get completedCount(): number { return this.counts.completedCount; }
  get rejectedCount(): number { return this.counts.rejectedCount; }
  get missingCount(): number { return this.counts.missingCount; }
  get needsImprovementCount(): number { return this.counts.needsImprovementCount; }
  get allCount(): number { return this.counts.allCount; }

  // ---------- Filters ----------

  setTypeFilter(type: ActionPointTypeFilter): void {
    this.typeFilter = type;
    // An interval only means something among recurring rows. Cleared rather
    // than left dangling, so the summary line can never claim a filter that
    // is not being applied.
    if (type !== 'recurring') {
      this.recurrenceFilter = null;
    }
    this.reload();
  }

  /** Chips toggle independently — click an active one to drop just that status. */
  toggleStatusFilter(status: FlexiComplianceActionPointStatus): void {
    if (this.statusFilter.has(status)) {
      this.statusFilter.delete(status);
    } else {
      this.statusFilter.add(status);
    }
    // Reassign so change detection sees a new reference.
    this.statusFilter = new Set(this.statusFilter);
    this.reload();
  }

  isStatusActive(status: FlexiComplianceActionPointStatus): boolean {
    return this.statusFilter.has(status);
  }

  /**
   * The two findings a checkpoint can still owe work for, in severity order.
   * Green is deliberately absent: an action under a resolved checkpoint is not
   * something anyone filters the action plan looking for.
   */
  readonly allFindings: FlexiComplianceRating[] = [
    FlexiComplianceRating.Missing,
    FlexiComplianceRating.NeedsImprovement
  ];

  /** Chips toggle independently, same as the status ones. */
  toggleFindingFilter(rating: FlexiComplianceRating): void {
    if (this.findingsFilter.has(rating)) {
      this.findingsFilter.delete(rating);
    } else {
      this.findingsFilter.add(rating);
    }
    // Reassign so change detection sees a new reference.
    this.findingsFilter = new Set(this.findingsFilter);
    this.reload();
  }

  isFindingActive(rating: FlexiComplianceRating): boolean {
    return this.findingsFilter.has(rating);
  }

  /** Reuses the rating labels the rating view already ships in every language. */
  findingKey(rating: FlexiComplianceRating): string {
    switch (rating) {
      case FlexiComplianceRating.Missing: return 'FLEXI_COMPLIANCE.RATINGS.MISSING';
      case FlexiComplianceRating.NeedsImprovement: return 'FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT';
      default: return 'FLEXI_COMPLIANCE.RATINGS.GOOD';
    }
  }

  /** Traffic-light tone for the source badge on a row and for the chips. */
  findingToneClass(rating: FlexiComplianceRating | null): string {
    switch (rating) {
      case FlexiComplianceRating.Missing: return 'is-red';
      case FlexiComplianceRating.NeedsImprovement: return 'is-yellow';
      case FlexiComplianceRating.Good: return 'is-green';
      default: return '';
    }
  }

  /** How many rows sit under this finding, for the chip's count. */
  findingCount(rating: FlexiComplianceRating): number {
    return rating === FlexiComplianceRating.Missing
      ? this.counts.missingCount
      : this.counts.needsImprovementCount;
  }

  /**
   * Choosing an interval implies the recurring type, so the segment follows
   * automatically — otherwise picking "Annually" while on One-off would filter
   * to an empty set and look broken.
   */
  setRecurrenceFilter(type: FlexiComplianceRecurrenceType | null): void {
    this.recurrenceFilter = type;
    if (type !== null) {
      this.typeFilter = 'recurring';
    }
    this.reload();
  }

  get hasActiveFilters(): boolean {
    return ( this.typeFilter !== 'all'
      || this.statusFilter.size > 0
      || this.findingsFilter.size > 0
      || this.recurrenceFilter !== null
    );
  }

  clearAllFilters(): void {
    this.typeFilter = 'all';
    this.statusFilter = new Set();
    this.findingsFilter = new Set();
    this.recurrenceFilter = null;
    this.reload();
  }

  /**
   * Drop the filters without refetching. Used by revealHighlighted, which is
   * about to issue its own request for the focused row's page and must not race
   * a second one against it.
   */
  private clearFiltersLocally(): void {
    this.typeFilter = 'all';
    this.statusFilter = new Set();
    this.findingsFilter = new Set();
    this.recurrenceFilter = null;
  }

  /**
   * What is actually being filtered on, in words, for the summary line. Stating
   * it beats making the user infer it from which chips look pressed — and it is
   * the accessible version of that information.
   */
  get activeFilterLabels(): string[] {
    const labels: string[] = [];

    if (this.typeFilter === 'recurring') {
      labels.push(this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TYPE_RECURRING'));
    } else if (this.typeFilter === 'oneOff') {
      labels.push(this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.TYPE_ONE_OFF'));
    }

    // Follow the enum's own order so the labels read Open → In progress →
    // Completed regardless of the order they were clicked in.
    for (const s of this.allStatuses) {
      if (this.statusFilter.has(s)) {
        labels.push(this.translate.instant(this.statusKey(s)));
      }
    }

    // Severity order, not click order, so "Mangler, Bør forbedres" always
    // reads the same way round.
    for (const f of this.allFindings) {
      if (this.findingsFilter.has(f)) {
        labels.push(this.translate.instant(this.findingKey(f)));
      }
    }

    if (this.recurrenceFilter !== null) {
      labels.push(this.translate.instant(recurrenceTypeKey(this.recurrenceFilter)));
    }

    return labels;
  }

  // ---------- Menu (status dropdown + delete) ----------

  toggleMenu(rowId: number, event?: Event): void {
    event?.stopPropagation();
    this.openMenuId = this.openMenuId === rowId ? null : rowId;
  }

  closeMenu(): void { this.openMenuId = null; }

  // ---------- Mutations ----------

  changeStatus(row: FlexiComplianceActionPointListItem, next: FlexiComplianceActionPointStatus, event?: Event): void {
    event?.stopPropagation();
    if (!this.canEditRow(row) || this.busyIds.has(row.id) || row.status === next) {
      this.openMenuId = null;
      return;
    }

    // Completing a recurring action point spawns the next occurrence, and if
    // this one is late the user has to say which schedule that follows. Ask the
    // server first: it owns the arithmetic, so the dates in the dialog are the
    // dates that get written.
    if (next === FlexiComplianceActionPointStatus.Completed
        && row.recurrenceType !== FlexiComplianceRecurrenceType.None) {
      this.openScheduleChoice(row);
      return;
    }

    this.applyStatus(row, next);
  }

  /**
   * Loads the deadline preview and opens the dialog. A non-late completion
   * needs no decision, so it completes straight through — prompting for
   * something with one possible answer just trains people to click past it.
   */
  private openScheduleChoice(row: FlexiComplianceActionPointListItem): void {
    this.openMenuId = null;
    this.scheduleChoiceLoading = true;
    this.error = null;
    this.service.previewNextDeadline(BaseServices.BusinessId, row.id).subscribe({
      next: preview => {
        this.scheduleChoiceLoading = false;
        if (!preview.requiresScheduleChoice) {
          this.applyStatus(row, FlexiComplianceActionPointStatus.Completed,
            FlexiComplianceScheduleMode.OriginalSchedule);
          return;
        }
        this.schedulePreview = preview;
        this.scheduleChoiceRow = row;
        this.selectedScheduleMode = FlexiComplianceScheduleMode.OriginalSchedule;
      },
      error: (err: HttpErrorResponse) => {
        this.scheduleChoiceLoading = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  closeScheduleChoice(): void {
    this.scheduleChoiceRow = null;
    this.schedulePreview = null;
  }

  /** Confirms the dialog with whichever schedule the user selected. */
  confirmScheduleChoice(): void {
    const row = this.scheduleChoiceRow;
    if (!row) return;
    const mode = this.selectedScheduleMode;
    this.closeScheduleChoice();
    this.applyStatus(row, FlexiComplianceActionPointStatus.Completed, mode);
  }

  /** The actual PUT. Shared by the plain path and the post-dialog path. */
  private applyStatus(
    row: FlexiComplianceActionPointListItem,
    next: FlexiComplianceActionPointStatus,
    scheduleMode?: FlexiComplianceScheduleMode
  ): void {
    const previousStatus = row.status;
    // Optimistic — flip in place so the badge reacts immediately.
    row.status = next;
    this.busyIds.add(row.id);
    this.openMenuId = null;
    this.service.updateActionPoint(BaseServices.BusinessId, row.id, {
      status: next,
      scheduleMode: scheduleMode ?? null
    }).subscribe({
      next: updated => {
        // Re-merge the server's truth (status, completedOn/By etc.) so the
        // "Completed by X on Y" surface stays accurate after a status flip.
        this.replaceRow(updated.id, {
          ...row, ...updated,
          sectionId: row.sectionId,
          sectionTitle: row.sectionTitle,
          sourceQuestion: row.sourceQuestion,
          sourceRating: row.sourceRating
        });
        this.busyIds.delete(row.id);
        // Completing a recurring one generated its successor server-side, and
        // completing the last outstanding one under a red or yellow checkpoint
        // promotes that checkpoint to green — either way the list in hand is
        // stale, and a row still badged "Mangler" after its finding was
        // resolved is worse than a reload.
        const promotesFinding = row.sourceRating === FlexiComplianceRating.Missing
          || row.sourceRating === FlexiComplianceRating.NeedsImprovement;
        if (next === FlexiComplianceActionPointStatus.Completed
            && (row.recurrenceType !== FlexiComplianceRecurrenceType.None || promotesFinding)) {
          this.load();
        }
      },
      error: (err: HttpErrorResponse) => {
        // Roll back the optimistic change so the UI matches the server.
        row.status = previousStatus;
        this.busyIds.delete(row.id);
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  /**
   * The window for editing details (title / description / owner / deadline)
   * and for deleting: CSM-only, and only while the action point is still
   * outstanding.
   *
   * Once completed, an action point records what was agreed and delivered —
   * the BE freezes those fields (409) and refuses the delete. Re-open it to
   * edit; deleting the whole review is the only way to remove a completed
   * one, and that cascades over them on purpose.
   *
   * Note this is stricter than <c>canEditRow</c>, which lets the customer-side
   * responsible person flip STATUS on their own commitments.
   */
  canManageRow(row: FlexiComplianceActionPointListItem): boolean {
    // Rejected counts as closed alongside Completed. The server refuses a
    // detail edit and a delete on both, so offering either here would hand the
    // user a form that can only end in a 409.
    return this.canEdit
      && row.status !== this.ApStatus.Completed
      && row.status !== this.ApStatus.Rejected;
  }

  /**
   * Row click. Opens the editor for rows the user can change, and the read-only
   * details dialog for everything else — a completed action point, or one
   * belonging to someone else, is still worth reading. Previously those rows
   * swallowed the click and looked broken.
   */
  onRowClick(row: FlexiComplianceActionPointListItem): void {
    if (this.canManageRow(row)) {
      this.openEdit(row);
      return;
    }
    this.openDetails(row);
  }

  openDetails(row: FlexiComplianceActionPointListItem, event?: Event): void {
    event?.stopPropagation();
    this.openMenuId = null;
    this.detailRow = row;
  }

  closeDetails(): void {
    this.detailRow = null;
  }

  openEdit(row: FlexiComplianceActionPointListItem, event?: Event): void {
    event?.stopPropagation();
    if (!this.canManageRow(row)) return;
    this.openMenuId = null;
    this.editRow = row;
    this.editForm = {
      title: row.title,
      description: row.description ?? '',
      responsible: row.responsibleParty ?? this.Responsible.Customer,
      due: row.dueDate ?? '',
      recurrenceType: row.recurrenceType ?? FlexiComplianceRecurrenceType.None
    };
  }

  closeEdit(): void {
    if (this.editSaving) return;
    this.editRow = null;
  }

  saveEdit(): void {
    const row = this.editRow;
    const title = this.editForm.title.trim();
    if (!row || !title || this.editSaving) return;
    const due = this.editForm.due.trim();
    // The server rejects a recurring action point with no deadline — catch it
    // here so the user gets a message next to the field instead of a 422.
    if (this.editForm.recurrenceType !== FlexiComplianceRecurrenceType.None && !due) {
      this.error = this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.DEADLINE_REQUIRED');
      return;
    }
    this.editSaving = true;
    this.error = null;
    this.service.updateActionPoint(BaseServices.BusinessId, row.id, {
      title,
      // Empty string clears the description server-side.
      description: this.editForm.description.trim(),
      responsibleParty: this.editForm.responsible,
      dueDate: due || null,
      // A null dueDate means "unchanged", so an emptied field needs the flag.
      clearDueDate: !due,
      recurrenceType: this.editForm.recurrenceType
    }).subscribe({
      next: updated => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        // The response is the plain action point — re-graft the list-only
        // context columns (source question, originating review) so the row
        // keeps rendering them.
        this.replaceRow(row.id, {
          ...row,
          ...updated,
          sectionId: row.sectionId,
          sectionTitle: row.sectionTitle,
          sourceQuestion: row.sourceQuestion
        });
        this.editSaving = false;
        this.editRow = null;
      },
      error: (err: HttpErrorResponse) => {
        this.editSaving = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_UPDATE_FAILED');
      }
    });
  }

  deleteRow(row: FlexiComplianceActionPointListItem, event?: Event): void {
    event?.stopPropagation();
    if (!this.canManageRow(row) || this.busyIds.has(row.id)) return;
    this.openMenuId = null;
    this.confirm.confirm({
      message: this.translate.instant('FLEXI_COMPLIANCE.CONFIRM.DELETE_ACTION'),
      accept: () => {
        this.busyIds.add(row.id);
        this.service.deleteActionPoint(BaseServices.BusinessId, row.id).subscribe({
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.DELETED');
            this.busyIds.delete(row.id);
            // Refetch rather than splice: the page is a window onto a longer
            // list, so dropping a row locally would leave nine rows on screen
            // and the tallies and page count stale.
            this.load();
          },
          error: (err: HttpErrorResponse) => {
            this.busyIds.delete(row.id);
            this.error = err.error?.detail
              ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.ACTION_DELETE_FAILED');
          }
        });
      }
    });
  }

  /**
   * Opens the audit timeline. This is where a "skipped cycles" or "schedule
   * shifted" entry is read, so it is reachable from every row rather than only
   * from the ones currently showing a warning.
   */
  openTimeline(row: FlexiComplianceActionPointListItem, event?: Event): void {
    event?.stopPropagation();
    this.openMenuId = null;
    this.timelineRow = row;
    this.timelineEntries = [];
    this.timelineLoading = true;
    this.service.actionPointTimeline(BaseServices.BusinessId, row.id).subscribe({
      next: entries => {
        this.timelineEntries = entries;
        this.timelineLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.timelineLoading = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.TIMELINE_FAILED');
      }
    });
  }

  closeTimeline(): void {
    this.timelineRow = null;
    this.timelineEntries = [];
  }

  private replaceRow(id: number, fresh: FlexiComplianceActionPointListItem): void {
    this.rows = this.rows.map(r => ( r.id === id ? fresh : r));
  }

  // ---------- View helpers ----------

  statusKey(s: number): string {
    switch (s) {
      case this.ApStatus.Completed: return 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_COMPLETED';
      case this.ApStatus.InProgress: return 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_IN_PROGRESS';
      case this.ApStatus.Rejected: return 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_REJECTED';
      default: return 'FLEXI_COMPLIANCE.ACTION_POINTS_TAB.STATUS_OPEN';
    }
  }

  statusToneClass(s: number): string {
    switch (s) {
      case this.ApStatus.Completed: return 'is-done';
      case this.ApStatus.InProgress: return 'is-progress';
      // Deliberately not the "done" tone: rejected work was never delivered,
      // and a green badge would read as if it had been.
      case this.ApStatus.Rejected: return 'is-rejected';
      default: return 'is-open';
    }
  }

  statusIcon(s: number): string {
    switch (s) {
      case this.ApStatus.Completed: return 'pi-check-circle';
      case this.ApStatus.InProgress: return 'pi-clock';
      case this.ApStatus.Rejected: return 'pi-ban';
      default: return 'pi-circle';
    }
  }

  responsibleKey(r: number | null): string | null {
    if (r == null) return null;
    return r === this.Responsible.Advisor
      ? 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR'
      : 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER';
  }

  /**
   * " - Kari Nordmann" to append after a translated role label, so a row says
   * who owes the work and not just which side does. Empty when the business has
   * nobody in the role. Kept a suffix so the role half stays in the translate
   * pipe — see responsiblePartyNameSuffix.
   */
  responsibleNameSuffix(row: FlexiComplianceActionPointListItem): string {
    return responsiblePartyNameSuffix(row);
  }

  /**
   * Surface overdue items in the meta strip — only meaningful for open /
   * in-progress rows. Compared via ISO string slice to dodge timezone
   * silliness; if the customer's clock is wrong by a day, the overdue
   * highlight may flip — acceptable, the row still shows the date.
   */
  isOverdue(row: FlexiComplianceActionPointListItem): boolean {
    // A closed row is not late, whether it was delivered or written off —
    // chasing a deadline nobody is going to meet any more is just noise.
    if (!row.dueDate
        || row.status === this.ApStatus.Completed
        || row.status === this.ApStatus.Rejected) return false;
    const today = new Date().toISOString().slice(0, 10);
    return row.dueDate < today;
  }

  /**
   * The full lifecycle, in reading order. Drives the status filter chips and
   * the order labels appear in; the per-row dropdown narrows it — see
   * <c>statusesFor</c>.
   */
  readonly allStatuses: FlexiComplianceActionPointStatus[] = [
    FlexiComplianceActionPointStatus.Open,
    FlexiComplianceActionPointStatus.InProgress,
    FlexiComplianceActionPointStatus.Completed,
    FlexiComplianceActionPointStatus.Rejected
  ];

  /** The three states a customer may move their own commitment between. */
  private readonly customerStatuses: FlexiComplianceActionPointStatus[] = [
    FlexiComplianceActionPointStatus.Open,
    FlexiComplianceActionPointStatus.InProgress,
    FlexiComplianceActionPointStatus.Completed
  ];

  /**
   * Permission gate for status changes on an individual row. CSMs (Admin /
   * assigned HSE advisor) can always edit; non-CSM business users — Editors,
   * Customers, AND Portal users acting on the customer side (incl. a Portal
   * user R-moded in from the portal) — can update their own commitments,
   * i.e. APs where <c>responsibleParty === Customer</c>. This lets the
   * customer-side responsible person mark their action points complete. The
   * status dropdown is the only exposed mutation for non-CSMs; delete remains
   * CSM-only (gated separately via <c>*ngIf="canEdit"</c>). The backend
   * enforces the same rule in <c>UpdateActionPointAsync</c>.
   */
  canEditRow(row: FlexiComplianceActionPointListItem): boolean {
    if (this.canEdit) return true;
    // A written-off commitment stays written off until an advisor says
    // otherwise. Letting the customer re-open one would put a previous
    // review's work back on the plan, which is the exact thing rejecting it
    // was for.
    if (row.status === this.ApStatus.Rejected) return false;
    // The customer-side responsible person manages their own commitments.
    // Additionally, ANY overdue commitment may be closed out by a business
    // user so outstanding items never get stuck open past their deadline —
    // the backend records who completed it (CompletedBy) and still restricts
    // non-CSMs to status changes only (no relabel/reassign/reschedule).
    return ( row.responsibleParty === this.Responsible.Customer || this.isOverdue(row));
  }

  /**
   * Customers see all three of their own status options — they may need to
   * reopen something they marked Completed by mistake. "Avvist" is CSM-only:
   * writing a commitment off is a judgement about the review, not about the
   * task, and letting a customer pick it would turn "I am not going to do this"
   * into a way to clear the action plan. The backend refuses it too.
   * Delete is gated separately in the template via <c>*ngIf="canEdit"</c>.
   */
  statusesFor(_row: FlexiComplianceActionPointListItem): FlexiComplianceActionPointStatus[] {
    return this.canEdit ? this.allStatuses : this.customerStatuses;
  }

  // ---------- CSV export ----------

  /**
   * Build a CSV from the currently visible rows (i.e. honors the status
   * filter) and trigger a browser download. Pure client-side — no BE
   * endpoint needed since the AP list is already loaded.
   *
   * Encoded with a UTF-8 BOM so Excel opens the file with the right
   * character set on Windows (otherwise Norwegian å/æ/ø show as mojibake).
   * Quoting follows RFC 4180: only fields containing comma, quote, or
   * newline are wrapped; embedded quotes are doubled.
   */
  /**
   * Export every action point the current filters match — not the page.
   *
   * The grid is server-paged, so `rows` is ten of possibly hundreds. Writing a
   * file from it would silently truncate the export to whatever the user
   * happened to be looking at, which is the worst kind of wrong: the file looks
   * complete. So the whole list is fetched for the file and the filter bar is
   * re-applied to it here.
   */
  exportCsv(event?: Event): void {
    event?.stopPropagation();
    if (this.totalCount === 0 || this.exporting) return;

    this.exporting = true;
    this.service.listActionPoints(BaseServices.BusinessId).subscribe({
      next: all => {
        this.exporting = false;
        this.downloadCsv(all.filter(r => this.matchesFilters(r)));
      },
      error: (err: HttpErrorResponse) => {
        this.exporting = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.EXPORT_FAILED');
      }
    });
  }

  /**
   * The filter bar as a predicate, for the export only.
   *
   * The grid does not use this — the server filters the page. It exists because
   * the export fetches the unfiltered list and still has to honour what the
   * user is looking at.
   */
  private matchesFilters(row: FlexiComplianceActionPointListItem): boolean {
    if (this.typeFilter === 'recurring'
        && row.recurrenceType === FlexiComplianceRecurrenceType.None) return false;
    if (this.typeFilter === 'oneOff'
        && row.recurrenceType !== FlexiComplianceRecurrenceType.None) return false;
    if (this.statusFilter.size > 0 && !this.statusFilter.has(row.status)) return false;
    // A loose action point has no checkpoint, so it matches no finding.
    if (this.findingsFilter.size > 0
        && (row.sourceRating === null || !this.findingsFilter.has(row.sourceRating))) return false;
    if (this.recurrenceFilter !== null && row.recurrenceType !== this.recurrenceFilter) return false;
    return true;
  }

  /**
   * Download the action points as a branded XLSX workbook.
   *
   * Separate from the CSV rather than replacing it: the CSV is a plain data
   * dump anything can parse, while this one is a presentation document with the
   * business's logo in the header. Both honour the current filters.
   */
  exportXlsx(event?: Event): void {
    event?.stopPropagation();
    if (this.totalCount === 0 || this.exportingXlsx) return;

    this.exportingXlsx = true;
    this.service.exportActionPointsXlsx(BaseServices.BusinessId, {
      // Paging is required by the query type but ignored by the endpoint — the
      // file covers every matching row.
      pageNumber: 1,
      pageSize: this.pageSize,
      isRecurring: this.isRecurringFilterValue,
      statuses: Array.from(this.statusFilter),
      sourceRatings: Array.from(this.findingsFilter),
      recurrenceType: this.recurrenceFilter
    }).subscribe({
      next: blob => {
        this.exportingXlsx = false;
        this.saveBlob(blob, this.buildXlsxFilename());
      },
      error: (err: HttpErrorResponse) => {
        this.exportingXlsx = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.EXPORT_FAILED');
      }
    });
  }

  private buildXlsxFilename(): string {
    const today = new Date().toISOString().slice(0, 10);
    return `flexi-action-points-${BaseServices.BusinessId}-${today}.xlsx`;
  }

  /** Hands a generated blob to the browser as a download. */
  private saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private downloadCsv(exportRows: FlexiComplianceActionPointListItem[]): void {
    if (exportRows.length === 0) return;

    const headers = [
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_TITLE'),
      'Description',
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_SOURCE'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_REVIEW'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_RESPONSIBLE'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_DUE'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_STATUS'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_CREATED'),
      'Created by',
      this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.LABEL'),
      this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.SKIPPED_CYCLES'),
      this.translate.instant('FLEXI_COMPLIANCE.RECURRENCE.SCHEDULE_SHIFTED'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_RESPONSIBLE_PERSON'),
      this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.COLUMN_HSE_ADVISOR')
    ];
    const looseLabel = this.translate.instant('FLEXI_COMPLIANCE.ACTION_POINTS_TAB.SOURCE_LOOSE');

    const rows = exportRows.map(r => {
      const source = r.sourceQuestion
        ?r.sectionTitle ? `${r.sectionTitle} — ${r.sourceQuestion}` : r.sourceQuestion
        : looseLabel;
      const responsibleKey = this.responsibleKey(r.responsibleParty);
      const responsible = responsibleKey
        ? this.translate.instant(responsibleKey) + responsiblePartyNameSuffix(r)
        : '';
      // The list spans every review, so the export has to say which one each
      // commitment came from or the rows lose their context.
      const reviewDate = (r.reviewCompletedOn ?? r.reviewCreatedOn ?? '').slice(0, 10);
      const review = [r.catalogVersionCode, reviewDate].filter(Boolean).join(' · ');
      return [
        r.title,
        r.description ?? '',
        source,
        review,
        responsible,
        r.dueDate ?? '',
        this.translate.instant(this.statusKey(r.status)),
        r.createdOn?.slice(0, 10) ?? '',
        r.createdByName ?? '',
        // recurrenceName is resolved server-side, so the export matches the
        // grid exactly rather than re-deriving the label from the enum.
        r.recurrenceName ?? '',
        r.skippedCycles ? String(r.skippedCycles) : '',
        r.scheduleShifted
          ? this.translate.instant('SWITCH_YES')
          : '',
        r.responsibleUserName ?? '',
        r.hseAdvisorName ?? ''
      ];
    });

    const csv = [headers, ...rows]
      .map(cols => cols.map(this.csvCell).join(','))
      .join('\r\n');

    // BOM forces Excel into UTF-8 mode on Windows.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.buildCsvFilename();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private csvCell(value: string): string {
    if (value == null) return '';
    // Quote if the value contains comma, quote, CR, LF.
    if (/[",\r\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private buildCsvFilename(): string {
    const today = new Date().toISOString().slice(0, 10);
    const businessId = BaseServices.BusinessId;
    // Name the file after what is in it: the export honours the active
    // filters, so two exports from the same day must not collide.
    const scope = this.typeFilter === 'all' ? '' : `${this.typeFilter === 'oneOff' ? 'one-off' : 'recurring'}-`;
    return `${scope}action-points-${businessId}-${today}.csv`;
  }
}
